import { CLIENT_TYPES } from '../consts/authentication';
import {
  LOCAL_STORAGE_KEYS,
  CROSS_DOMAIN_REDIRECT_TTL_MS,
} from '../consts/localStorage';
import {
  OIDC_AUTHENTICATION_ENDPOINTS,
  OIDC_PROVIDER,
} from '../network/httpClients/OIDCAuthenticationClient/consts';
import { getAuthTrackingParams } from '../pages/Login/utils';
import { QUERY_PARAMS } from '../routes/consts';
import { Token } from '../types/token';
import { handleOIDCError } from './authentication';
import {
  getAuthTokensFromCookie,
  hasAuthTokenCookie,
  removeAccessTokenCookie,
} from './cookies';
import { getDynamicSetting, getAppUrl } from './dynamicSettings';
import { isAirgappedMode } from './env';
import { clearPkceCodeVerifier, getPkceCodeVerifier } from './pkce';
import {
  getQueryParam,
  isErrorPage,
  isLoginPage,
  isSuccessSigninExternalClientPage,
  shouldPersistAuthData,
  getTraceIdFromSessionStorage,
  getSessionIdFromSessionStorage,
  buildSearchParams,
  removeMarketingParamsFromLocalStorage,
} from './url';

/**
 * Stores OAuth tokens in localStorage with calculated expiry time
 * @param token - The token data containing access token, refresh token, and expiry time
 */
export const storeTokens = (token: Token): void => {
  localStorage.removeItem(LOCAL_STORAGE_KEYS.CROSS_DOMAIN_REDIRECT_IN_PROGRESS);

  if (!shouldPersistAuthData()) {
    return;
  }

  // TODO:: Is the expires_in in seconds or milliseconds?
  const expiresInMS = token.expires_in * 1000;
  const expiry = Date.now() + expiresInMS;

  localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, token.access_token);
  localStorage.setItem(LOCAL_STORAGE_KEYS.ID_TOKEN, token.id_token);
  localStorage.setItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN, token.refresh_token);
  localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY, expiry.toString());
};

/**
 * Whether a cross-domain 409 handoff is in flight at this origin, i.e. we are the source and
 * the browser has not navigated away yet. During that window the tokens live only in the
 * shared cookie, so callers must not treat their absence from localStorage as a signed-out
 * state.
 *
 * Returns false when storage is unreadable (blocked/disabled): callers use this to suppress
 * destructive work, so an unreadable marker must not stop a sign-out from cleaning up. A
 * broken store also means the marker was never written, so there is no handoff to protect.
 */
export const isCrossDomainRedirectActive = (): boolean => {
  try {
    const timestamp = localStorage.getItem(
      LOCAL_STORAGE_KEYS.CROSS_DOMAIN_REDIRECT_IN_PROGRESS,
    );

    return (
      timestamp !== null &&
      Date.now() - Number(timestamp) < CROSS_DOMAIN_REDIRECT_TTL_MS
    );
  } catch {
    return false;
  }
};

/**
 * Restores access token and refresh token from cookie to localStorage after a cross-subdomain redirect.
 * Retrieves the tokens from cookie and saves them to localStorage.
 *
 * Cookie removal is conditional: removed on a successful restore or when a readable cookie holds the
 * invalid 'null' sentinel. It is KEPT when the read is ambiguous (absent/partial/transient error) or
 * when localStorage persistence fails, so a later call can retry (returns undefined that call).
 *
 * Returns undefined without consuming the cookie if a cross-domain redirect is in progress at this
 * origin (i.e. we are the source, not the destination). This prevents retry requests at the source
 * portal from eating the migration cookie before the browser has navigated away.
 *
 * @returns The access token that was restored, or undefined if none found or persistence failed
 */
const saveTokenFromCookieToLocalStorage = (): string | undefined => {
  if (isCrossDomainRedirectActive()) {
    // No log here: getAccessToken() runs on every interceptor request, so logging
    // during the redirect window would flood the console. Transient, expected state.
    return undefined;
  }

  // Clear any expired redirect guard before attempting restoration
  localStorage.removeItem(LOCAL_STORAGE_KEYS.CROSS_DOMAIN_REDIRECT_IN_PROGRESS);

  const cookieData = getAuthTokensFromCookie();

  const isPresentButInvalid =
    !!cookieData &&
    (cookieData.accessToken === 'null' || cookieData.refreshToken === 'null');

  if (isPresentButInvalid) {
    // We read the cookie and it holds the 'null' sentinel — sweep it so it can't
    // override a valid localStorage token with a "Bearer null" header.
    removeAccessTokenCookie();

    return undefined;
  }

  if (!cookieData) {
    // Absent, partial, or a transient read error — indistinguishable here, and all
    // three fall back to localStorage. Do NOT remove: deleting on a read blip would
    // destroy the migration cookie a later call needs. No error log: this is the
    // expected fallback path on every interceptor request during partial-cookie states.
    return undefined;
  }

  try {
    console.info('Login: Restoring data');

    localStorage.setItem(
      LOCAL_STORAGE_KEYS.ACCESS_TOKEN,
      cookieData.accessToken,
    );
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.REFRESH_TOKEN,
      cookieData.refreshToken,
    );

    if (cookieData.idToken) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.ID_TOKEN, cookieData.idToken);
    }

    if (cookieData.tokenExpiry) {
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.TOKEN_EXPIRY,
        cookieData.tokenExpiry,
      );
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY);
    }

    removeAccessTokenCookie();

    return cookieData.accessToken;
  } catch (error) {
    console.error(
      'Failed to persist migrated token to localStorage; keeping cookie for retry.',
      error,
    );

    return undefined;
  }
};

/**
 * Retrieves the access token, preferring a present cross-domain migration cookie
 * (fresh, written during a cross-subdomain 409 redirect) over this origin's
 * possibly-stale localStorage. Gates on ANY auth cookie so the restore path also
 * cleans up a partial/orphaned fragment. Falls back to localStorage when no cookie
 * is present, or when restoration yields nothing (no valid cookie / source-origin
 * redirect guard active).
 *
 * @returns The access token string or undefined if neither source has one
 */
export const getAccessToken = (): string | undefined => {
  const accessToken = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
  const localAccessToken =
    accessToken && accessToken !== 'null' ? accessToken : undefined;

  if (hasAuthTokenCookie()) {
    return restoreTokenFromCookieIfNeeded() ?? localAccessToken;
  }

  return localAccessToken;
};

const restoreTokenFromCookieIfNeeded = (): string | undefined => {
  try {
    return saveTokenFromCookieToLocalStorage();
  } catch (error) {
    console.error(
      'Failed to restore token from cookie. Keeping cookie to retry later.',
      error,
    );

    return undefined;
  }
};

/**
 * Retrieves the refresh token from localStorage
 * @returns The refresh token string or      if not found or invalid
 */
export const getRefreshToken = (): string | undefined => {
  const refreshToken = localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);

  if (!refreshToken || refreshToken === 'null') {
    return undefined;
  }

  return refreshToken;
};

/**
 * Retrieves the OIDC ID token from localStorage. Used as the `id_token_hint`
 * for the OIDC end-session endpoint.
 * @returns The ID token string or undefined if not found or invalid
 */
export const getIdToken = (): string | undefined => {
  const idToken = localStorage.getItem(LOCAL_STORAGE_KEYS.ID_TOKEN);

  if (!idToken || idToken === 'null') {
    return undefined;
  }

  return idToken;
};

/**
 * Checks if the current access token is expired or will expire within 3 minutes
 * @returns True if token is expired or will expire soon, false otherwise
 */
export const isTokenExpired = (): boolean => {
  const expiry = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY);
  const BUFFER_IN_MS = 3 * 60 * 1000;

  if (!expiry) return true;

  // Add  buffer before expiry
  return Date.now() > parseInt(expiry) - BUFFER_IN_MS;
};

/**
 * Removes all OAuth tokens from localStorage
 */
export const clearTokens = (): void => {
  localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.ID_TOKEN);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY);
};

/**
 * Mint a fresh id_token for the CLI ST-adoption /device/confirm and persist it where the
 * Authorization interceptor reads it. The migrated id_token may be expired by the time the
 * ST landing confirms (verified in prod: "Token expired" → GAP-3 400 → CLI polls forever).
 *
 * Does its OWN refresh fetch rather than refreshAccessToken(), which clears ALL tokens on
 * failure — a transient blip must not wipe the still-usable migrated token and send confirm
 * with no Bearer. On success persists access + id + refresh + expiry (the backend may rotate
 * the refresh_token). On any failure, leaves existing tokens untouched (best-effort).
 */
export const refreshTokenForDeviceConfirm = async (): Promise<void> => {
  if (isAirgappedMode()) {
    return;
  }

  try {
    // Inside the try: getRefreshToken()/getSessionIdFromSessionStorage() read storage,
    // which can throw when it's blocked/disabled — a throw must degrade to best-effort,
    // not bubble up and break the ST landing.
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      return;
    }

    const authUrlOidc = getDynamicSetting('auth_url_oidc');
    const response = await fetch(
      `${authUrlOidc}/${OIDC_AUTHENTICATION_ENDPOINTS.TOKEN}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: buildSearchParams({
          [QUERY_PARAMS.GRANT_TYPE]: 'refresh_token',
          [QUERY_PARAMS.REFRESH_TOKEN]: refreshToken,
          [QUERY_PARAMS.TRACE_ID]: getTraceIdFromSessionStorage(),
          [QUERY_PARAMS.SESSION_ID]: getSessionIdFromSessionStorage() ?? '',
        }),
      },
    );

    if (!response.ok) {
      return; // leave existing tokens in place — do NOT clear on a transient failure
    }

    const token: Token = await response.json();

    // Require BOTH access_token and id_token: the /device/confirm Bearer IS the id_token,
    // so a response missing it is useless — and writing an undefined id_token would clobber
    // the still-valid migrated one. Leave existing tokens untouched in that case.
    if (!token?.access_token || !token?.id_token) {
      return;
    }

    // Persist directly: storeTokens() no-ops on the external-client success page
    // (shouldPersistAuthData=false). Write all four so a rotated refresh_token isn't dropped.
    localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, token.access_token);
    localStorage.setItem(LOCAL_STORAGE_KEYS.ID_TOKEN, token.id_token);

    if (token.refresh_token) {
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.REFRESH_TOKEN,
        token.refresh_token,
      );
    }

    if (token.expires_in) {
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.TOKEN_EXPIRY,
        (Date.now() + token.expires_in * 1000).toString(),
      );
    }
  } catch {
    // Network/parse blip — leave existing tokens untouched; confirm proceeds with what's
    // there and the manual fallback remains available.
  }
};

/**
 * Refreshes the access token using the refresh token. Stores the new tokens in localStorage.
 * @returns Promise resolving to new token data or undefined if refresh failed
 */
export const refreshAccessToken = async (): Promise<Token | undefined> => {
  if (isAirgappedMode()) {
    return undefined;
  }

  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    return undefined;
  }

  try {
    const authUrlOidc = getDynamicSetting('auth_url_oidc');

    const response = await fetch(
      `${authUrlOidc}/${OIDC_AUTHENTICATION_ENDPOINTS.TOKEN}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: buildSearchParams({
          [QUERY_PARAMS.GRANT_TYPE]: 'refresh_token',
          [QUERY_PARAMS.REFRESH_TOKEN]: refreshToken,
          [QUERY_PARAMS.TRACE_ID]: getTraceIdFromSessionStorage(),
          [QUERY_PARAMS.SESSION_ID]: getSessionIdFromSessionStorage() || '',
        }),
      },
    );

    if (!response.ok) {
      throw new Error('Refresh token request failed');
    }

    const token = await response.json();

    storeTokens(token);

    return token;
  } catch (error) {
    console.error('Failed to refresh token:', error);
    clearTokens();

    return undefined;
  }
};

/**
 * Fetches the token from the server and store it in localStorage
 * @returns The token object or undefined if not found
 */
export const fetchToken = (): Promise<Token | undefined> => {
  const code = getQueryParam(QUERY_PARAMS.CODE);
  let provider = getQueryParam(QUERY_PARAMS.PROVIDER) as string;
  const state = getQueryParam(QUERY_PARAMS.STATE) as string;

  if (isAirgappedMode() && !provider && code) {
    provider = OIDC_PROVIDER.OIDC;
  }

  if (
    isErrorPage() ||
    (isLoginPage() && !isSuccessSigninExternalClientPage())
  ) {
    return new Promise((resolve) => {
      resolve(undefined);
    });
  }

  if (isAirgappedMode() && !code) {
    return new Promise((resolve) => {
      resolve(undefined);
    });
  }

  if ([code, provider].some((item) => !item)) {
    // No OAuth callback params to exchange — not an error. Resolve undefined so
    // the auth flow falls through to the login/userinfo path, matching the other
    // "nothing to exchange" branches above (error page, login page, airgapped-no-code).
    return new Promise((resolve) => {
      resolve(undefined);
    });
  }

  const tokenParams = new URLSearchParams({
    grant_type: 'authorization_code',
    code: code || '',
    redirect_uri: getAppUrl(),
    state,
    provider,
    client_id: CLIENT_TYPES.PORTAL,
    ...Object.fromEntries(getAuthTrackingParams()),
  });

  if (isAirgappedMode()) {
    const codeVerifier = getPkceCodeVerifier();

    if (codeVerifier) {
      tokenParams.set(QUERY_PARAMS.CODE_VERIFIER, codeVerifier);
    }
  }

  const authUrlOidc = getDynamicSetting('auth_url_oidc');

  return fetch(`${authUrlOidc}/${OIDC_AUTHENTICATION_ENDPOINTS.TOKEN}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams(tokenParams),
  })
    .then(async (tokenRes) => {
      if (!tokenRes.ok) {
        const errorData = await tokenRes.json();

        throw errorData;
      }

      removeMarketingParamsFromLocalStorage();

      return tokenRes.json() as Promise<Token>;
    })
    .then((token) => {
      storeTokens(token);

      if (isAirgappedMode()) {
        clearPkceCodeVerifier();
      }

      // Clean the URL to remove code, state, and provider parameters
      // This prevents re-attempting token exchange if the page reloads
      const url = new URL(window.location.href);

      url.searchParams.delete(QUERY_PARAMS.CODE);
      url.searchParams.delete(QUERY_PARAMS.STATE);
      url.searchParams.delete(QUERY_PARAMS.PROVIDER);
      url.searchParams.delete(QUERY_PARAMS.TRACE_ID);
      url.searchParams.delete('access_type'); // Remove this too
      window.history.replaceState({}, '', url.toString());

      return token;
    })
    .catch((error) => {
      handleOIDCError(error);

      return undefined;
    });
};
